import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { calendarService } from '@/services/calendarService';
import type { DayAvailability, Booking } from '@/types';

interface CalendarViewProps {
  providerId: string;
  onBookingClick?: (booking: Booking) => void;
  onTimeSlotClick?: (date: string, startTime: string, endTime: string) => void;
}

export function CalendarView({ providerId, onBookingClick, onTimeSlotClick }: CalendarViewProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'week' | 'month'>('week');
  const [availability, setAvailability] = useState<DayAvailability[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load calendar data
  useEffect(() => {
    loadCalendarData();
  }, [currentDate, viewMode, providerId]);

  const loadCalendarData = async () => {
    setIsLoading(true);
    try {
      const { startDate, endDate } = getDateRange();
      
      console.log('Loading calendar data for range:', startDate, 'to', endDate);
      
      const [availabilityData, bookingsData] = await Promise.all([
        calendarService.getAvailabilityForRange(providerId, startDate, endDate),
        calendarService.getProviderBookings(providerId, startDate, endDate)
      ]);

      console.log('Loaded bookings:', bookingsData);
      
      setAvailability(availabilityData);
      setBookings(bookingsData);
    } catch (error) {
      console.error('Failed to load calendar data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getDateRange = () => {
    const start = new Date(currentDate);
    const end = new Date(currentDate);

    if (viewMode === 'week') {
      // Get start of week (Monday)
      const dayOfWeek = start.getDay();
      const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      start.setDate(diff);
      
      // Get end of week (Sunday)
      end.setDate(start.getDate() + 6);
    } else {
      // Get start of month
      start.setDate(1);
      
      // Get end of month
      end.setMonth(end.getMonth() + 1);
      end.setDate(0);
    }

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    };
  };

  const navigateDate = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    
    if (viewMode === 'week') {
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
    } else {
      newDate.setMonth(newDate.getMonth() + (direction === 'next' ? 1 : -1));
    }
    
    setCurrentDate(newDate);
  };

  const getWeekDays = () => {
    const start = new Date(currentDate);
    const dayOfWeek = start.getDay();
    const diff = start.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    start.setDate(diff);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getDayAvailability = (date: Date): DayAvailability | undefined => {
    const dateStr = date.toISOString().split('T')[0];
    return availability.find(day => day.date === dateStr);
  };

  const getDayBookings = (date: Date): Booking[] => {
    const dateStr = date.toISOString().split('T')[0];
    const filtered = bookings.filter(booking => {
      const bookingDateStr = booking.scheduledDate instanceof Date 
        ? booking.scheduledDate.toISOString().split('T')[0]
        : new Date(booking.scheduledDate).toISOString().split('T')[0];
      return bookingDateStr === dateStr;
    });
    
    // Debug logging
    if (filtered.length > 0) {
      console.log(`Found ${filtered.length} bookings for ${dateStr}:`, filtered);
    }
    
    return filtered;
  };

  const formatDateHeader = () => {
    if (viewMode === 'week') {
      const weekDays = getWeekDays();
      const start = weekDays[0];
      const end = weekDays[6];
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
    } else {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div className="flex items-center space-x-4">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Calendar className="w-5 h-5 mr-2" />
            {formatDateHeader()}
          </h2>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => navigateDate('prev')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigateDate('next')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex items-center space-x-3">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('week')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'week' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Week
            </button>
            <button
              onClick={() => setViewMode('month')}
              className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                viewMode === 'month' 
                  ? 'bg-white text-gray-900 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Month
            </button>
          </div>
        </div>
      </div>

      {/* Calendar Content */}
      <div className="p-4">
        {viewMode === 'week' ? (
          <WeekView
            days={getWeekDays()}
            getDayAvailability={getDayAvailability}
            getDayBookings={getDayBookings}
            onBookingClick={onBookingClick}
            onTimeSlotClick={onTimeSlotClick}
          />
        ) : (
          <MonthView
            currentDate={currentDate}
            getDayAvailability={getDayAvailability}
            getDayBookings={getDayBookings}
            onBookingClick={onBookingClick}
          />
        )}
      </div>
    </div>
  );
}

// Week View Component
function WeekView({ 
  days, 
  getDayAvailability, 
  getDayBookings, 
  onBookingClick, 
  onTimeSlotClick 
}: {
  days: Date[];
  getDayAvailability: (date: Date) => DayAvailability | undefined;
  getDayBookings: (date: Date) => Booking[];
  onBookingClick?: (booking: Booking) => void;
  onTimeSlotClick?: (date: string, startTime: string, endTime: string) => void;
}) {
  // Calculate time range based on bookings and availability
  const calculateTimeRange = () => {
    let minHour = 8; // Default start
    let maxHour = 18; // Default end
    
    // Check all bookings for the week
    days.forEach(day => {
      const dayBookings = getDayBookings(day);
      dayBookings.forEach(booking => {
        const startHour = parseInt(booking.startTime.split(':')[0]);
        const endHour = parseInt(booking.endTime.split(':')[0]);
        
        minHour = Math.min(minHour, startHour);
        maxHour = Math.max(maxHour, endHour + 1); // +1 to show the end hour
      });
      
      // Check availability schedule
      const dayAvail = getDayAvailability(day);
      if (dayAvail?.timeSlots && dayAvail.timeSlots.length > 0) {
        dayAvail.timeSlots.forEach(slot => {
          const startHour = parseInt(slot.startTime.split(':')[0]);
          const endHour = parseInt(slot.endTime.split(':')[0]);
          minHour = Math.min(minHour, startHour);
          maxHour = Math.max(maxHour, endHour);
        });
      }
    });
    
    // Ensure reasonable bounds
    minHour = Math.max(6, minHour); // Not earlier than 6 AM
    maxHour = Math.min(23, maxHour); // Not later than 11 PM
    
    return { minHour, maxHour };
  };
  
  const { minHour, maxHour } = calculateTimeRange();
  const timeSlots = Array.from({ length: maxHour - minHour }, (_, i) => {
    const hour = minHour + i;
    return `${hour.toString().padStart(2, '0')}:00`;
  });

  return (
    <div className="grid grid-cols-8 gap-1">
      {/* Time column header */}
      <div className="text-center text-sm font-medium text-gray-500 py-2">
        Time
      </div>
      
      {/* Day headers */}
      {days.map((day) => (
        <div key={day.toISOString()} className="text-center py-2">
          <div className="text-sm font-medium text-gray-900">
            {day.toLocaleDateString('en-US', { weekday: 'short' })}
          </div>
          <div className={`text-lg font-semibold ${
            day.toDateString() === new Date().toDateString() 
              ? 'text-primary-600' 
              : 'text-gray-700'
          }`}>
            {day.getDate()}
          </div>
        </div>
      ))}

      {/* Time slots */}
      {timeSlots.map((time) => (
        <div key={time} className="contents">
          {/* Time label */}
          <div className="text-sm text-gray-500 py-2 text-right pr-2">
            {time}
          </div>
          
          {/* Day columns */}
          {days.map((day) => {
            const dayAvailability = getDayAvailability(day);
            const dayBookings = getDayBookings(day);
            const timeBooking = dayBookings.find(booking => 
              booking.startTime <= time && booking.endTime > time
            );

            return (
              <div
                key={`${day.toISOString()}-${time}`}
                className={`min-h-[60px] border border-gray-100 p-1 ${
                  dayAvailability?.available && !dayAvailability.isBlackout
                    ? 'bg-green-50 hover:bg-green-100 cursor-pointer'
                    : 'bg-gray-50'
                }`}
                onClick={() => {
                  if (dayAvailability?.available && !timeBooking && onTimeSlotClick) {
                    const endTime = `${(parseInt(time.split(':')[0]) + 1).toString().padStart(2, '0')}:00`;
                    onTimeSlotClick(day.toISOString().split('T')[0], time, endTime);
                  }
                }}
              >
                {timeBooking && (
                  <div
                    className={`text-xs p-1 rounded cursor-pointer ${
                      timeBooking.status === 'CONFIRMED' 
                        ? 'bg-blue-100 text-blue-800 border border-blue-200'
                        : timeBooking.status === 'PENDING'
                        ? 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                        : 'bg-gray-100 text-gray-800 border border-gray-200'
                    }`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onBookingClick?.(timeBooking);
                    }}
                  >
                    <div className="font-medium truncate">{timeBooking.customerName}</div>
                    <div className="truncate">{timeBooking.serviceType}</div>
                  </div>
                )}
                
                {dayAvailability?.isBlackout && (
                  <div className="text-xs text-gray-500 p-1">
                    {dayAvailability.blackoutReason}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// Month View Component
function MonthView({ 
  currentDate, 
  getDayAvailability, 
  getDayBookings, 
  onBookingClick 
}: {
  currentDate: Date;
  getDayAvailability: (date: Date) => DayAvailability | undefined;
  getDayBookings: (date: Date) => Booking[];
  onBookingClick?: (booking: Booking) => void;
}) {
  const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());

  const days = [];
  const current = new Date(startDate);
  
  for (let i = 0; i < 42; i++) { // 6 weeks
    days.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  return (
    <div className="grid grid-cols-7 gap-1">
      {/* Day headers */}
      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
        <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
          {day}
        </div>
      ))}

      {/* Calendar days */}
      {days.map((day) => {
        const isCurrentMonth = day.getMonth() === currentDate.getMonth();
        const isToday = day.toDateString() === new Date().toDateString();
        const dayAvailability = getDayAvailability(day);
        const dayBookings = getDayBookings(day);

        return (
          <div
            key={day.toISOString()}
            className={`min-h-[100px] border border-gray-100 p-2 ${
              isCurrentMonth ? 'bg-white' : 'bg-gray-50'
            } ${dayAvailability?.available ? 'hover:bg-gray-50' : ''}`}
          >
            <div className={`text-sm font-medium mb-1 ${
              isToday 
                ? 'text-primary-600' 
                : isCurrentMonth 
                ? 'text-gray-900' 
                : 'text-gray-400'
            }`}>
              {day.getDate()}
            </div>

            {dayAvailability?.isBlackout && (
              <div className="text-xs text-red-600 mb-1">
                Unavailable
              </div>
            )}

            <div className="space-y-1">
              {dayBookings.slice(0, 3).map((booking) => (
                <div
                  key={booking.id}
                  className={`text-xs p-1 rounded cursor-pointer truncate ${
                    booking.status === 'CONFIRMED' 
                      ? 'bg-blue-100 text-blue-800'
                      : booking.status === 'PENDING'
                      ? 'bg-yellow-100 text-yellow-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                  onClick={() => onBookingClick?.(booking)}
                >
                  {booking.startTime} {booking.customerName}
                </div>
              ))}
              
              {dayBookings.length > 3 && (
                <div className="text-xs text-gray-500">
                  +{dayBookings.length - 3} more
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}